import { TemplatePortal } from '@angular/cdk/portal';
import { NgStyle } from '@angular/common';
import {
  AfterContentInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  output,
  signal,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIcon } from '../icon/icon';
import { HxSegmentComponent } from './segment';
import { HxSegmentBodyComponent } from './segment-body';
import { HxSegment, HxSegmentSelectionEvent } from './types';

/**
 * Segmented Control - Accessible tab-like selector with single selection, customizable styling, icons, disabled states,
 * and keyboard navigation.
 */
@Component({
  selector: 'hx-segmented-control',
  standalone: true,
  imports: [NgStyle, HxIcon, HxSegmentBodyComponent],
  templateUrl: './segmented-control.html',
  styleUrls: ['./segmented-control.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxSegmentedControlComponent extends HxBaseControl implements AfterContentInit {
  // Content projection queries
  readonly segmentComponents = contentChildren(HxSegmentComponent);

  // Injected dependencies
  private readonly viewContainerRef = inject(ViewContainerRef);

  // Inputs (segments input optional for backwards compatibility)
  readonly segments = input<HxSegment[]>([]);
  override readonly ariaLabel = input<string>('Segmented control');
  readonly defaultSelectedKey = input<string | undefined>(undefined);

  // Content area inputs
  readonly animationDuration = input<number>(300);
  readonly dynamicHeight = input(false, { transform: booleanAttribute });
  /** Whether to show the content area. Set to false when using external content (e.g., router-outlet). */
  readonly showContent = input<boolean>(true);

  // Styling inputs
  readonly fontSize = input<'sm' | 'md' | 'lg' | 'xl' | undefined>(undefined);

  // Behavior inputs
  readonly variant = input<'filled' | 'outline' | 'underline'>('filled');
  readonly textAlignment = input<'left' | 'center' | 'right'>('center');
  readonly widthMode = input<'equal-width' | 'content-fit'>('equal-width');
  readonly responsiveMode = input<'wrap' | 'scroll' | 'shrink'>('wrap');
  readonly allowDeselection = input(false, { transform: booleanAttribute });
  readonly disabledOpacity = input<number>(0.5);
  readonly showNotAllowedCursor = input(true, { transform: booleanAttribute });

  // Output
  readonly selectionChange = output<HxSegmentSelectionEvent>();

  // Internal state signals
  readonly internalSelectedKey = signal<string | null>(null);
  readonly focusedKey = signal<string | null>(null);
  readonly validatedSegments = signal<HxSegment[]>([]);

  // Content area state
  readonly contentPortals = signal<(TemplatePortal | null)[]>([]);
  readonly loadedSegmentKeys = signal<Set<string>>(new Set());
  readonly uniqueId = `hx-segmented-control-${Math.random().toString(36).substr(2, 9)}`;

  // Computed signal for animation duration as string with 'ms' or 's' suffix
  readonly animationDurationMs = computed(() => {
    const duration = this.animationDuration();
    if (typeof duration === 'number') {
      return `${duration}ms`;
    }
    return duration || '300ms';
  });

  constructor() {
    super();
    // Effect to handle lazy loading of content when selection changes
    effect(() => {
      const selectedKey = this.internalSelectedKey();
      if (selectedKey) {
        const loaded = this.loadedSegmentKeys();
        if (!loaded.has(selectedKey)) {
          const newLoaded = new Set(loaded);
          newLoaded.add(selectedKey);
          this.loadedSegmentKeys.set(newLoaded);
          // Update portals after adding to loaded set
          this.updateContentPortals();
        }
      }
    });
  }

  // Computed signals
  protected readonly selectedIndex = computed(() => {
    const key = this.internalSelectedKey();
    return this.validatedSegments().findIndex((seg) => seg.key === key);
  });

  readonly containerClasses = computed(() => {
    const classes: string[] = ['hx-segmented-control'];

    const wm = this.widthMode();
    if (wm) {
      classes.push(`hx-segmented-control--${wm}`);
    }

    const fs = this.fontSize();
    if (fs) {
      classes.push(`hx-segmented-control--font-${fs}`);
    }
    const rm = this.responsiveMode();
    if (rm) {
      classes.push(`hx-segmented-control--${rm}`);
    }
    const v = this.variant();
    if (v) {
      classes.push(`hx-segmented-control--${v}`);
    }
    const ta = this.textAlignment();
    if (ta) {
      classes.push(`hx-segmented-control--align-${ta}`);
    }

    return classes;
  });

  readonly containerStyles = computed(() => {
    const styles: Record<string, string> = {};

    const fs = this.fontSize();
    if (fs) {
      styles['--component-segmented-control-font-size'] = fs;
    }
    const dop = this.disabledOpacity();
    if (dop !== undefined) {
      styles['--component-segmented-control-disabled-opacity'] = this.clamp(dop, 0, 1).toString();
    }

    return styles;
  });

  ngAfterContentInit(): void {
    // Validate and process segments (now content children are available)
    const validated = this.validateSegments(this.getSegmentsData());
    this.validatedSegments.set(validated);

    // Set initial selection
    const initialKey = this.getInitialSelectedKey(validated);
    this.internalSelectedKey.set(initialKey);

    // Mark initial selected segment as loaded
    if (initialKey) {
      const loadedKeys = new Set<string>();
      loadedKeys.add(initialKey);
      this.loadedSegmentKeys.set(loadedKeys);
    }

    // Initialize content portals after content children are available
    this.updateContentPortals();
  }

  /** Gets segments data from @Input segments or child HxSegment components. */
  protected getSegmentsData(): HxSegment[] {
    const inputSegments = this.segments();
    if (inputSegments && inputSegments.length > 0) {
      return inputSegments;
    }

    const components = this.segmentComponents();
    return components.map((comp) => ({
      key: comp.getKey(),
      label: comp.label(),
      icon: comp.icon(),
      badgeCount: comp.badgeCount() ?? undefined,
      disabled: comp.disabled(),
      ariaLabel: comp.ariaLabel(),
    }));
  }

  /** Updates content portals for lazy-loaded content. */
  protected updateContentPortals(): void {
    const components = this.segmentComponents();
    if (!components || components.length === 0) {
      return;
    }

    const segments = this.validatedSegments();
    const loadedKeys = this.loadedSegmentKeys();

    const portals = segments.map((segment, index) => {
      const hasTemplate = components[index] !== undefined;
      const isLoaded = loadedKeys.has(segment.key);
      const shouldCreate = hasTemplate && isLoaded;

      if (shouldCreate) {
        return new TemplatePortal(components[index].contentTemplate(), this.viewContainerRef);
      }
      return null;
    });

    this.contentPortals.set(portals);
  }

  /**
   * Gets the position of a segment relative to the selected segment. Returns -1 (left), 0 (center/selected), or 1
   * (right).
   */
  getSegmentPosition(segmentIndex: number): number {
    const selected = this.selectedIndex();
    if (selected === -1) {
      return 1; // No selection, hide all content (position right)
    }
    if (segmentIndex < selected) {
      return -1; // Left
    }
    if (segmentIndex > selected) {
      return 1; // Right
    }
    return 0; // Center/selected
  }

  /** Generates a unique ID for a segment button. */
  getSegmentId(segmentKey: string): string {
    return `${this.uniqueId}-segment-${segmentKey}`;
  }

  /** Generates a unique ID for a segment content area. */
  getContentId(segmentKey: string): string {
    return `${this.uniqueId}-content-${segmentKey}`;
  }

  /**
   * Validates and processes segment array.
   *
   * - Enforces max 10 segments
   * - Detects duplicate keys
   * - Truncates labels to 50 characters
   */
  protected validateSegments(segments: HxSegment[]): HxSegment[] {
    if (segments.length > 10) {
      console.warn(`[hx-segmented-control] Maximum 10 segments allowed. Only first 10 will be rendered.`);
      segments = segments.slice(0, 10);
    }

    // Check for duplicate keys
    const keys = new Set<string>();
    const duplicates: string[] = [];
    segments.forEach((seg) => {
      if (keys.has(seg.key)) {
        duplicates.push(seg.key);
      }
      keys.add(seg.key);
    });

    if (duplicates.length > 0) {
      console.error(`[hx-segmented-control] Duplicate segment keys detected: ${duplicates.join(', ')}`);
    }

    // Truncate labels to 50 characters
    return segments.map((seg) => ({
      ...seg,
      label: seg.label.length > 50 ? seg.label.substring(0, 50) : seg.label,
    }));
  }

  /** Determines the initial selected key based on inputs and configuration. */
  protected getInitialSelectedKey(segments: HxSegment[]): string | null {
    // Priority 1: defaultSelectedKey input
    const defaultKey = this.defaultSelectedKey();
    if (defaultKey) {
      const matchingSegment = segments.find((seg) => seg.key === defaultKey);
      if (matchingSegment) {
        return defaultKey;
      } else {
        console.error(
          `[hx-segmented-control] defaultSelectedKey "${defaultKey}" does not match ` +
            `any segment key. Falling back to first segment.`
        );
      }
    }

    // Priority 2: First non-disabled segment
    const firstNonDisabled = segments.find((seg) => !seg.disabled);
    return firstNonDisabled ? firstNonDisabled.key : null;
  }

  /** Clamps a number between min and max values. */
  protected clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  /** Handles segment click events. */
  handleSegmentClick(segment: HxSegment): void {
    if (segment.disabled) {
      return;
    }

    const previousKey = this.internalSelectedKey();
    const isAlreadySelected = previousKey === segment.key;

    // Handle deselection mode
    if (isAlreadySelected && this.allowDeselection() === true) {
      this.internalSelectedKey.set(null);
      this.selectionChange.emit({
        selectedKey: null,
        selectedIndex: -1,
        segment: null,
        previousKey,
      });
      return;
    }

    // Normal selection (prevent re-emitting if already selected)
    if (isAlreadySelected) {
      return;
    }

    this.internalSelectedKey.set(segment.key);
    const selectedIdx = this.validatedSegments().findIndex((seg) => seg.key === segment.key);

    this.selectionChange.emit({
      selectedKey: segment.key,
      selectedIndex: selectedIdx,
      segment,
      previousKey,
    });
  }

  /** Handles keyboard navigation on individual segment buttons. */
  handleSegmentKeydown(event: KeyboardEvent, segment: HxSegment): void {
    const segments = this.validatedSegments();
    if (segments.length === 0) {
      return;
    }

    const currentIndex = segments.findIndex((seg) => seg.key === segment.key);
    let targetIndex = -1;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowRight': {
        event.preventDefault();
        const direction = event.key === 'ArrowLeft' ? -1 : 1;
        targetIndex = (currentIndex + direction + segments.length) % segments.length;
        this.focusNextNonDisabled(segments, targetIndex, direction);
        return;
      }
      case 'Home': {
        event.preventDefault();
        this.focusNextNonDisabled(segments, 0, 1);
        return;
      }
      case 'End': {
        event.preventDefault();
        this.focusNextNonDisabled(segments, segments.length - 1, -1);
        return;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (!segment.disabled) {
          this.handleSegmentClick(segment);
        }
        return;
      }
    }
  }

  private focusNextNonDisabled(segments: HxSegment[], startIndex: number, direction: number): void {
    for (let i = 0; i < segments.length; i++) {
      const index = (startIndex + i * direction + segments.length) % segments.length;
      if (!segments[index].disabled) {
        const element = document.getElementById(this.getSegmentId(segments[index].key)) as HTMLButtonElement;
        element?.focus();
        return;
      }
    }
  }
}
